import { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from '@tanstack/react-router';
import * as React from 'react';
import { FundFormDialog } from '~/components/forms/FundFormDialog';
import { BreadcrumbProps } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { colTypesWidths } from '~/components/ui/tables/ag/columnTypes';
import {
  colDefsDocumentMasterDetTable,
  subTableDef,
} from '~/components/ui/tables/ag/documentsGroupTable/colDefsDocumentMasterDetTable';
import { hierarchyComputeMasterRow } from '~/components/ui/tables/ag/documentsGroupTable/computeMasterRowFromHierarchy';
import { getDocumentsGroupContextMenu } from '~/components/ui/tables/ag/documentsGroupTable/getDocumentsGroupContextMenu';
import {
  ComputedMasterRow,
  DocumentTableProps,
  ExtendedFund,
} from '~/components/ui/tables/ag/documentsGroupTable/types';
import {
  FlatHierarchy,
  flattenHierarchyArray,
} from '~/lib/fund/flattenHierarchyArray';
import { getFundByIdFromHierarchy } from '~/lib/fund/fundsToHierarchy';
import { getHierarchyPath } from '~/lib/fund/getHierarchyPath';
import { Fund } from '~/types';

export const TableAgDocumentsGroup = ({
  hierarchy,
  fundId,
}: DocumentTableProps) => {
  const [, setFlatHierarchy] = useState<FlatHierarchy[]>([]);
  const [, setBreadcrumbs] = useState<BreadcrumbProps | undefined>();
  const [isEditFundDialogVisible, setIsEditFundDialogVisible] = useState(false);
  const [fund, setFund] = useState<Fund | null | undefined>();
  const [expandFundId, setExpandFundId] = useState<string | undefined>();
  const gridRef = React.useRef<AgGridReact<ComputedMasterRow> | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (hierarchy && fundId) {
      const path = getHierarchyPath({
        hierarchy: hierarchy,
        level: 'fund',
        fundId,
      });
      setBreadcrumbs({
        items: [
          { name: 'Funds', url: '/documents' },
          { name: path?.fund?.name ?? 'not found' },
        ],
      });
    }
  }, [hierarchy, fundId]);

  useEffect(() => {
    if (hierarchy) {
      const flatHierarchy = flattenHierarchyArray(hierarchy);
      setFlatHierarchy(flatHierarchy);
    }
  }, [hierarchy]);

  useEffect(() => {
    if (gridRef.current && expandFundId) {
      gridRef.current.api.forEachNode((rowNode) => {
        const found = rowNode.data?.funds.find((rowFund) => {
          return rowFund.fundId === expandFundId; //fund?.fundId;
        });
        console.log('found', found);
        if (found) {
          console.log('found set expanded row', found);
          rowNode.setExpanded(true);
        }
      });
    }
  }, [expandFundId]);

  gridRef.current?.api?.forEachDetailGridInfo((detailGridInfo) => {
    detailGridInfo?.api?.clearFocusedCell();
    detailGridInfo?.api?.sizeColumnsToFit();
  });

  /** sub table context menu
   * */
  const ctxSubTableMenu = useCallback(
    (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      const showEditFundPopup = (fundId: string) => {
        if (hierarchy && fundId) {
          setFund(getFundByIdFromHierarchy(fundId, hierarchy));
          setIsEditFundDialogVisible(true);
        }
      };
      const goToFundPage = (fundId: string) => {
        navigate({ to: `/documents/fund/${fundId}` });
      };
      return getDocumentsGroupContextMenu(params, {
        goToFundPage: goToFundPage,
        showEditFundPopup: showEditFundPopup,
      });
    },
    [setFund, setIsEditFundDialogVisible, hierarchy, navigate],
  );

  /** Returns Definition of sub table columns
   * */
  const subTableDefCtx = useCallback(
    (e: GetContextMenuItemsParams<ExtendedFund>) => {
      return subTableDef(e, ctxSubTableMenu);
    },
    [ctxSubTableMenu],
  );

  const AGGridTable = useMemo(() => {
    if (!hierarchy) return null;
    return (
      <AgGridReact
        rowData={hierarchyComputeMasterRow(hierarchy)}
        masterDetail={true}
        detailCellRendererParams={subTableDefCtx}
        columnDefs={colDefsDocumentMasterDetTable}
        suppressCellFocus
        groupDefaultExpanded={1}
        columnTypes={colTypesWidths}
        tooltipShowDelay={0}
        onGridSizeChanged={() => {
          gridRef.current?.api.sizeColumnsToFit();
          gridRef.current?.api.forEachDetailGridInfo((detailGridInfo) => {
            detailGridInfo?.api?.sizeColumnsToFit();
          });
        }}
        theme={agTheme}
        ref={gridRef}
      />
    );
  }, [hierarchy, subTableDefCtx]);

  return (
    <div className="flex-column flex page-content-row">
      <div
        className={'ag-theme-quartz'}
        style={{ width: '100%', height: '80vh' }}
      >
        {AGGridTable}
        {fund && (
          <FundFormDialog
            formMode="edit"
            onHide={(fundId) => {
              setExpandFundId(fundId);
              setIsEditFundDialogVisible(false);
              setFund(null);
            }}
            visible={isEditFundDialogVisible}
            fundId={fund.fundId}
            initialValue={fund}
          />
        )}
      </div>
    </div>
  );
};
